/*
 * Copyright (c) 2024-2025. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import { STORAGE_KEYS } from 'constants/StorageKeys'
import { LocalStorage } from 'utils/LocalStorage'

export const getQueryParams = () => {
    const params: Record<string, string | string[]> = {}
    const search = window.location.search
    if (!search) return params

    const query = decodeURIComponent(search).replace(/^\?/, '')
    query.split('&').forEach((param) => {
        const [key, value] = param.split('=')
        if (key) {
            params[key] = params[key]
                ? ([value] as string[]).concat(params[key])
                : value
        }
    })
    return params
}

/**
 * A simple forEach() implementation for Arrays, Objects and NodeLists
 * @private
 * @param {Array|Object|NodeList} collection Collection of items to iterate
 * @param {Function} callback Callback function for each iteration
 * @param {Array|Object|NodeList} scope Object/NodeList/Array that forEach is iterating over (aka `this`)
 * @return {Array|Object}
 */
export const forEach = function (
    collection: any[] | Record<string, any>,
    callback: (value: unknown, key: string | number, collection: any[] | Record<string, any>) => unknown,
    scope: any = null
) {
    const result: any[] = []
    if (collection === null || typeof collection === 'undefined') {
        return result
    }
    if (Object.prototype.toString.call(collection) === '[object Object]') {
        for (const prop in collection) {
            if (Object.prototype.hasOwnProperty.call(collection, prop)) {
                result.push(
                    callback.call(scope, collection[prop], prop, collection)
                )
            }
        }
    } else {
        const array = collection as any[]
        for (let i = 0, len = array.length; i < len; i++) {
            result.push(callback.call(scope, array[i], i, collection))
        }
    }
    return result
}

//https://egghead.io/lessons/javascript-redux-persisting-the-state-to-the-local-storage
export const loadState = () => {
    try {
        const jsonState = LocalStorage.get<Record<string, any>>(STORAGE_KEYS.STATE)
        if (jsonState === null) {
            return undefined
        }

        const sessionState = {
            notifications: Array.isArray(jsonState.sessionState?.notifications)
                ? jsonState.sessionState.notifications
                : [],
            session: jsonState.sessionState?.session ?? null,
            profile: jsonState.sessionState?.profile ?? null,
            ranking: jsonState.sessionState?.ranking ?? null,
            result: jsonState.sessionState?.result ?? null,
            results: Array.isArray(jsonState.sessionState?.results)
                ? jsonState.sessionState.results
                : [],
            language: jsonState.sessionState?.language ?? null,
            themeMode: jsonState.sessionState?.themeMode ?? null,
            tickets: Array.isArray(jsonState.sessionState?.tickets)
                ? jsonState.sessionState.tickets
                : [],
            myStats: jsonState.sessionState?.myStats ?? {
                bestLap: [],
                me: null,
                stats: [],
            },
        }

        const ui = {
            blur: false,
            headbarTitle: null,
            modal: {
                isOpen: false,
                component: null,
                props: {},
            },
            secondaryModal: {
                isOpen: false,
                component: null,
                props: {},
                title: null,
            },
            message: null,
            infoMessage: null,
            remindersContext: null,
            tables:
                jsonState.ui && typeof jsonState.ui.tables === 'object'
                    ? jsonState.ui.tables
                    : {},
            tableFiltersVisible:
                jsonState.ui && typeof jsonState.ui.tableFiltersVisible === 'object'
                    ? jsonState.ui.tableFiltersVisible
                    : {},
            tableRows:
                jsonState.ui && typeof jsonState.ui.tableRows === 'object'
                    ? jsonState.ui.tableRows
                    : {},
        }

        return {
            sessionState,
            ui,
            tableViewsState: {
                views: [],
                hasLoaded: false,
                activeViewIds: {},
            },
            crmState: {
                rejectionReasons:
                    jsonState.crmState?.rejectionReasons ?? null,
                mergeAuditLog: Array.isArray(jsonState.crmState?.mergeAuditLog)
                    ? jsonState.crmState.mergeAuditLog
                    : [],
                importBacklog: Array.isArray(jsonState.crmState?.importBacklog)
                    ? jsonState.crmState.importBacklog
                    : [],
            },
        }
    } catch {
        return undefined
    }
}

export const saveState = (state: Record<string, any>) => {
    try {
        const compactState: Record<string, any> = {
            sessionState: {
                notifications: Array.isArray(state.sessionState?.notifications)
                    ? state.sessionState.notifications
                    : [],
                session: state.sessionState?.session ?? null,
                profile: state.sessionState?.profile ?? null,
                ranking: state.sessionState?.ranking ?? null,
                result: state.sessionState?.result ?? null,
                results: Array.isArray(state.sessionState?.results)
                    ? state.sessionState.results
                    : [],
                language: state.sessionState?.language ?? null,
                themeMode: state.sessionState?.themeMode ?? null,
                tickets: Array.isArray(state.sessionState?.tickets)
                    ? state.sessionState.tickets
                    : [],
                myStats: state.sessionState?.myStats ?? {
                    bestLap: [],
                    me: null,
                    stats: [],
                },
            },
            ui: {
                blur: false,
                modal: {
                    isOpen: false,
                    component: null,
                    props: {},
                },
                secondaryModal: {
                    isOpen: false,
                    component: null,
                    props: {},
                    title: null,
                },
                message: null,
                infoMessage: null,
                tables: state.ui?.tables ?? {},
                tableFiltersVisible: state.ui?.tableFiltersVisible ?? {},
                tableRows: state.ui?.tableRows ?? {},
                remindersContext: null,
                headbarTitle: null,
            },
            // Table views are owned by the backend — never persist them in localStorage.
            tableViewsState: { views: [], hasLoaded: false, activeViewIds: {} },
            crmState: {
                rejectionReasons: state.crmState?.rejectionReasons ?? null,
                mergeAuditLog: Array.isArray(state.crmState?.mergeAuditLog)
                    ? state.crmState.mergeAuditLog
                    : [],
                importBacklog: Array.isArray(state.crmState?.importBacklog)
                    ? state.crmState.importBacklog
                    : [],
            },
        }

        const saved = LocalStorage.set(STORAGE_KEYS.STATE, compactState)
        if (!saved) {
            // Hard fallback to ensure state key stays writable even in low-quota environments.
            const minimalState = {
                sessionState: {
                    session: state.sessionState?.session ?? null,
                    language: state.sessionState?.language ?? null,
                    themeMode: state.sessionState?.themeMode ?? null,
                },
            }

            LocalStorage.remove(STORAGE_KEYS.STATE)
            LocalStorage.set(STORAGE_KEYS.STATE, minimalState)
        }
    } catch {
        // ignore write errors
    }
}

export const profile = (what: string, callback: () => void) => {
    const startTime = performance.now()

    // Do the normal stuff for this function
    callback()

    const duration = performance.now() - startTime
    console.log(`${what} took ${duration}ms`)
}

export const Url = {
    serialize: function (obj: any, prefix?: string): string {
        const str: string[] = []
        for (const p in obj) {
            if (Object.prototype.hasOwnProperty.call(obj, p)) {
                const k = prefix ? prefix + '[' + p + ']' : p
                const v = obj[p]
                str.push(
                    v !== null &&
                        typeof v === 'object' &&
                        Object.keys(v).length > 0
                        ? Url.serialize(v, k)
                        : encodeURIComponent(k) + '=' + encodeURIComponent(v)
                )
            }
        }
        return str.join('&')
    },
}

export const selectMapper = (labelName: string, valueName: string) => {
    return (item: Record<string, any>) => ({
        label: item[labelName],
        value: item[valueName],
    })
}

export const throttle = (func: (...args: any[]) => void, limit: number) => {
    let inThrottle: boolean
    return function (this: any, ...args: any[]) {
        if (!inThrottle) {
            func.apply(this, args)
            inThrottle = true
            setTimeout(() => (inThrottle = false), limit)
        }
    }
}

export const bookingLoginModalCustomStyles = {
    content: {
        top: '50%',
        left: '50%',
        right: 'auto',
        bottom: 'auto',
        marginRight: '-50%',
        transform: 'translate(-50%, -50%)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        borderWidth: '0',
        padding: '0',
        margin: '0',
        background: 'transparent',
    },
    overlay: {
        backgroundColor: 'var(--color-helper-overlay-bg)',
        padding: '30px 15px',
        zIndex: 10000,
    },
}
